/**
 * DO EDITOR PARA A BIBLIOTECA — converter um `Comic` no formato publicado.
 *
 * T2.53: *"biblioteca deveria aparecer, se eu coloquei como público os meus quadrinhos, eles
 * deveriam aparecer aqui para as pessoas poderem ver os quadrinhos na plataforma."*
 *
 * ── O QUE A MEDIÇÃO REVELOU, E POR QUE ESTE ARQUIVO EXISTE ──────────────────────────────────
 *
 * Medi em 2026-09-12, antes de escrever: a leitura da biblioteca funciona e está provada — a
 * consulta, as regras, a tela. **O que não existia era o ato de publicar.** Fora dos tipos e de
 * uma fixture do demo de motores, `publicacao` não aparecia em lugar nenhum de `hq-lab/src`:
 * nenhuma tela escrevia na coleção `biblioteca`.
 *
 * Então `T2.53` é literal no pior sentido: *"se eu coloquei como público"* descreve uma ação que
 * não tinha como ser feita.
 *
 * ── DOIS FORMATOS, E ELES NÃO SÃO O MESMO ───────────────────────────────────────────────────
 *
 * O editor guarda `Comic` — páginas de `DraggableItem` do Konva, com `type` em inglês
 * (`scenarios`, `characters`, `textBubbles`, `rectangles`, `figures`) e a imagem referida por
 * `imgId`. A biblioteca lê `DocumentoHQ` v2 — páginas de `Elemento` tipado, com camadas.
 *
 * Este arquivo é a ponte, e ela é de mão única de propósito: publicar é um RETRATO. O que está na
 * biblioteca não volta para o editor; quem quer mudar edita o original e publica de novo. Uma
 * ponte de mão dupla exigiria que os dois formatos fossem equivalentes, e eles não são — o v2
 * sabe coisas que o Konva não guarda (motor, legenda, camada nomeada) e vice-versa.
 *
 * ── A IMAGEM É O PROBLEMA, E ELE É DE ARQUITETURA ───────────────────────────────────────────
 *
 * `getImgUrl(imgId)` devolve `this.imgs[id].url`, e essa `url` é **base64 guardado no documento
 * da conta** (`app-user.class.ts:107`). A coleção é privada: só o dono lê.
 *
 * Então publicar não pode apontar para lá — ninguém mais abriria. A imagem viaja com a página, e
 * o limite de 1 MiB por documento do Firestore passa a valer. O teto e a recusa com mensagem
 * estão em `publicacao.ts`; aqui o que importa é que `refImagem` carrega o base64 inteiro.
 *
 * Isto não é o conserto — é o limite dito em voz alta. O conserto é guardar imagem fora do
 * Firestore, e isso não é pedido em ponto nenhum do T2.
 */
import { Comic } from "../classes/comic.class";
import { TextBubble } from "../classes/text-bubble.class";
import { Rectangle } from "../classes/rectangle.class";
import AppUser from "../api/app-user.class";
import {
  Balao,
  Camada,
  Elemento,
  FORMATO_DOCUMENTO,
  FormatoPagina,
  Imagem,
  MotorCanvas,
  PADROES_PAGINA,
  Pagina,
  Quadro,
  Reticula,
  VERSAO_DOCUMENTO,
} from "../api/documento.types";

/** Um id estável por página e por elemento. O v2 exige `id` em elemento e camada. */
function idDe(prefixo: string, i: number): string {
  return `${prefixo}-${i}`;
}

/**
 * Converte um item do editor num elemento do formato publicado.
 *
 * Devolve `null` para o que não tem correspondente — e devolver nulo é melhor que inventar: um
 * elemento que a biblioteca não sabe desenhar apareceria como buraco sem explicação.
 */
function elementoDe(item: unknown, indice: number, appUser: AppUser): Elemento | null {
  // `item` chega como `DraggableItem`, e a distinção de tipo é por instância e não por campo:
  // `TextBubble` e `Rectangle` carregam dados próprios que o objeto base não tem.
  const base = item as {
    id?: string;
    type?: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation?: number;
    imgId?: string;
  };

  // O v2 guarda a caixa PLANA no elemento (`ElementoBase extends Caixa`), e nao aninhada. Os
  // nomes tambem sao outros: `width`/`height` do Konva sao `largura`/`altura` aqui.
  const caixa = {
    x: Math.round(base.x ?? 0),
    y: Math.round(base.y ?? 0),
    largura: Math.round(base.width ?? 0),
    altura: Math.round(base.height ?? 0),
    // ── `undefined` NÃO É UM VALOR QUE O FIRESTORE GUARDA, E ISSO DERRUBAVA O PUBLICAR ────────
    //
    // Medido em 27/09/2026 contra o Firestore local: publicar falhava com
    // `Function setDoc() called with invalid data. Unsupported field value: undefined (found in
    // document biblioteca/{id}/paginas/1)`. Nada era gravado da história; a ficha invisível da fase 1
    // ficava lá, e a tela dizia "Não deu para publicar agora".
    //
    // A causa é um desencontro de vocabulário, e ele é fácil de não ver: em TypeScript um campo
    // OPCIONAL (`rotacao?: number`) aceita "ausente" E "undefined" como a mesma coisa. Para o
    // Firestore são coisas diferentes — ausente ele guarda, `undefined` ele RECUSA o documento
    // inteiro. Escrever `rotacao: undefined` não é o mesmo que não escrever `rotacao`.
    //
    // Por isso a chave só entra quando tem valor. Vale para toda chave opcional deste módulo, e o
    // teste `sem-undefined.test.ts` varre o documento inteiro para a regra não se perder na próxima
    // que alguém acrescentar.
    ...(base.rotation ? { rotacao: Math.round(base.rotation) } : {}),
  };
  const id = base.id || idDe("el", indice);

  if (item instanceof TextBubble) {
    const balao: Balao = {
      id,
      tipo: "balao",
      ...caixa,
      texto: item.Text(),
      // O editor tem UM tipo de balão; o formato v2 tem cinco. Publicar como "fala", que é o
      // único que o editor de fato produz — e não como "narracao", que mudaria o sentido da
      // página sem ninguém pedir.
      estilo: "fala",
      crescePorTexto: true,
      tamanhoFonte: item.FontSize(),
      fonte: item.FontFamily(),
    };
    return balao;
  }

  if (item instanceof Rectangle) {
    const quadro: Quadro = {
      id,
      tipo: "quadro",
      ...caixa,
      traco: item.borderWidth,
      corTraco: item.borderColor,
      corFundo: item.fillColor,
      raio: item.cornerRadius,
    };
    return quadro;
  }

  // Cenário, personagem e figura são todos IMAGEM no formato publicado.
  //
  // `Personagem` existe no v2 e seria mais expressivo, mas ele exige um dos quatro personagens
  // nomeados do guia da marca — e o editor insere imagem do acervo por `imgId`, sem saber qual
  // personagem é. Publicar como `personagem` obrigaria a adivinhar, e adivinhar aqui trocaria o
  // desenho da criança por outro.
  const url = base.imgId ? appUser.getImgUrl(base.imgId) : "";
  if (!url) return null;

  const imagem: Imagem = {
    id,
    tipo: "imagem",
    ...caixa,
    refImagem: url,
    // A chave só entra quando há descrição. Ver a nota do `rotacao` acima: `undefined` recusa o
    // documento inteiro, e uma imagem sem descrição é uma chave ausente, não uma chave vazia.
    ...(base.type === "characters" ? { descricaoAcessivel: "Personagem da história" } : {}),
  };
  return imagem;
}

/**
 * As páginas do documento publicado, a partir do `Comic`.
 *
 * Uma camada por página, chamada "Publicado": o Konva não tem camadas nomeadas, e inventar três
 * camadas vazias para parecer com o v2 seria estrutura sem conteúdo. O motor é `grade-css`
 * porque é o que a biblioteca usa para LER — texto de verdade, acessível, buscável —, e não
 * `konva`, que é o de editar.
 */
/**
 * O MOTOR DE CADA PAGINA SAI DO FORMATO QUE A PESSOA ESCOLHEU — T2.94 e T2.95.
 *
 * T2.94: "ter a opcao quando voce vai montar um quadrinho, alem do grid, que voce escolhendo aqui
 * o comeco, QUAL TAMANHO VAI SER A PAGINA, ROLAGEM VERTICAL, etc."
 * T2.95: "e ter uma opcao da ferramenta que eu vou estar adotando, se vai ser essa CLASSICA aqui
 * ou se vai ser UM OUTRO TIPO DE CANVAS que voce ta implementando."
 *
 * ── O QUE A MEDICAO MOSTROU, E MUDOU O TAMANHO DA TASK ────────────────────────────────────
 *
 * O seletor de formato JA EXISTIA (`PADROES_PAGINA`, seis opcoes), e uma delas e literalmente
 * "Rolagem vertical — para celular". E os quatro motores JA DESENHAVAM desde 09/09. O que nao
 * existia era a LIGACAO: escolher "rolagem vertical" mudava as dimensoes da folha e nada mais —
 * a pagina publicada saia desenhada por `grade-css` como todas as outras.
 *
 * Ou seja: a escolha estava na tela e nao chegava ao resultado. "Rolagem vertical" produzia uma
 * folha alta e estreita LIDA como pagina, e nao como rolagem.
 *
 * ── A LIGACAO, E ELA E DO PROPRIO FORMATO ────────────────────────────────────────────────
 *
 * Nao inventei o mapa: ele esta no comentario de `motores/index.tsx`, que descreve para que cada
 * motor serve —
 *   `fluxo-vertical`  "rolagem continua, um quadro por tela. E como crianca le no celular"
 *   `svg`             "vetor, escala sem perder nitidez. CAPA e exportacao"
 *   `grade-css`       "quadros em grade CSS. Texto real, acessivel, imprimivel. TIRINHA e prancha"
 *
 * Cada formato ja dizia qual era o dele; faltava alguem ler.
 */
const MOTOR_DO_FORMATO: Record<FormatoPagina, MotorCanvas> = {
  tirinha: "grade-css",
  pagina: "grade-css",
  "grade-livre": "grade-css",
  webtoon: "fluxo-vertical",
  capa: "svg",
  didatica: "grade-css",
};

/**
 * O formato da pagina, deduzido das DIMENSOES do `Comic`.
 *
 * O `Comic` do editor nao guarda formato — guarda largura e altura. Antes eu escrevia
 * `grade-livre` para tudo e dizia que era o honesto; e era, enquanto o motor tambem era fixo.
 * Agora que o motor DEPENDE do formato, escrever `grade-livre` sempre jogaria toda pagina em
 * `grade-css` — e a rolagem vertical continuaria nao existindo no resultado.
 *
 * Deduzir da proporcao nao e adivinhar: `PADROES_PAGINA` declara largura e altura de cada um, e a
 * comparacao e contra aqueles numeros. O que nao casar com nenhum cai em `grade-livre`, que e o
 * que "folha redimensionada a mao" de fato e.
 */
function formatoDasDimensoes(largura: number, altura: number): FormatoPagina {
  for (const id of Object.keys(PADROES_PAGINA) as FormatoPagina[]) {
    const p = PADROES_PAGINA[id];
    if (p.largura === largura && p.altura === altura) return id;
  }
  return "grade-livre";
}

export function paginasPublicaveis(
  comic: Comic,
  appUser: AppUser,
  reticula: Reticula = "nenhuma"
): Pagina[] {
  return comic.Pages().map((layer, iPagina) => {
    const elementos: Elemento[] = [];
    layer.items.forEach((item, iItem) => {
      const el = elementoDe(item, iItem, appUser);
      if (el) elementos.push(el);
    });

    const camada: Camada = {
      id: idDe("camada", iPagina),
      nome: "Publicado",
      visivel: true,
      elementos,
    };

    const formato = formatoDasDimensoes(layer.width, layer.height);

    const pagina: Pagina = {
      id: idDe("pagina", iPagina),
      formato,
      // O motor SAI do formato. Ver `MOTOR_DO_FORMATO` acima para por que o mapa não é meu.
      motor: MOTOR_DO_FORMATO[formato],
      largura: layer.width,
      altura: layer.height,
      // T2.108 — "aplicar retícula em TODAS as páginas, se quiser". A escolha é uma, e vale para
      // o quadrinho inteiro: é disso que a "uniformidade" que ele pede depende. Uma retícula por
      // página seria o oposto — cada folha com um acabamento diferente.
      reticula,
      camadas: [camada],
    };
    return pagina;
  });
}

/** A ficha — o que a biblioteca lê sem conta. Sem `paginas` dentro: a regra recusa. */
export function fichaPublicavel(entrada: {
  comic: Comic;
  appUser: AppUser;
  titulo: string;
  publicacao: unknown;
  quantasPaginas: number;
  /**
   * A capa, quando quem publica marcou a caixa. `T8.17`, saida (A), escolhida por ele em 28/09.
   *
   * OPCIONAL de verdade, e a chave nao entra quando nao ha: `undefined` recusaria a ficha inteira —
   * e a nota longa do `rotacao` em `elementoDe` existe porque isso ja aconteceu neste arquivo.
   */
  capa?: string | null;
}) {
  return {
    formato: FORMATO_DOCUMENTO,
    versao: VERSAO_DOCUMENTO,
    id: entrada.comic.getId(),
    titulo: entrada.titulo,
    criadoEm: new Date().toISOString(),
    atualizadoEm: new Date().toISOString(),
    autor: {
      id: entrada.appUser.Id(),
      nome: entrada.appUser.Name(),
      // `turma` recebe a escola porque é o que a biblioteca mostra no cartão, e é o que a
      // plataforma sabe. O campo se chama `turma` desde o v2 e renomeá-lo mexeria em todo
      // documento já publicado.
      // Sem escola declarada, a chave NÃO entra: `undefined` recusaria a ficha inteira. Ver a nota
      // longa do `rotacao` em `elementoDe`.
      ...(entrada.appUser.EducationalInstitution()
        ? { turma: entrada.appUser.EducationalInstitution() }
        : {}),
    },
    publicacao: entrada.publicacao,
    // A CONTAGEM VIAJA NA FICHA, e não é redundância: a biblioteca mostra "3 páginas" no cartão
    // sem abrir a história — que é justamente o que a divisão ficha/páginas existe para permitir.
    quantasPaginas: entrada.quantasPaginas,
    // A CAPA ATRAVESSA A LINHA DA FICHA, e por isso ela so entra quando foi consentida nesta
    // publicacao. Ver `capa-da-ficha.ts` para o que isso muda no desenho de privacidade.
    ...(entrada.capa ? { capa: entrada.capa } : {}),
  };
}
